import { faultSymptomsCallBlock } from './fault-symptoms'

/**
 * Below-the-fold relationship bundle for a single `:Equipment`
 * (`$equipmentId` param). Kept separate from
 * `PLANT_EQUIPMENT_DETAIL_CYPHER` so the header query stays bounded
 * and these panels can load independently.
 *
 * Returns one row:
 *   - `bomParts` / `bomEdges` — `HAS_PART` walk depth 1..8 (Part nodes
 *     + edge rows in `extractPlantEdges` shape). Client truncates to
 *     500 parts with ancestor closure.
 *   - `subEquipment` — direct child Equipment via outbound `HAS_EQUIPMENT`
 *   - `connections` — undirected `CONNECTS_TO` peer Equipment
 *   - `procedures` — inbound `PROCEDURE_FOR`
 *   - `faults` — `{ symptom, exhibits_status, causes[] }` from the
 *     equipment's `EXHIBITS_SYMPTOM` edges; causes are derived through
 *     Problems (`(sym)<-[:INDUCES]-(:Problem)-[:CAUSED_BY {confirmed}]->(cause)`)
 *     with a per-cause confirmation count
 *   - `areas` — `LOCATED_IN` targets labeled `:Area` only
 *   - `mesRecipes` / `mesPhases` / `mesBatches` / `mesOperations` —
 *     inbound MES usage families
 *
 * Bounded by `LIMIT 10000`.
 */
export const PLANT_EQUIPMENT_RELATIONSHIPS_CYPHER = `
  MATCH (eq:Equipment {id: $equipmentId})

  CALL {
    WITH eq
    OPTIONAL MATCH (eq)-[:HAS_PART*1..8]->(part:Part)
    WITH eq, [p IN collect(DISTINCT part) WHERE p IS NOT NULL] AS bomParts
    OPTIONAL MATCH (src)-[hp:HAS_PART]->(tgt:Part)
      WHERE (src = eq OR src IN bomParts) AND tgt IN bomParts
    RETURN bomParts,
           [e IN collect(DISTINCT {
             type: type(hp),
             source: src.id,
             target: tgt.id,
             props: properties(hp)
           }) WHERE e.source IS NOT NULL AND e.target IS NOT NULL] AS bomEdges
  }

  CALL {
    WITH eq
    OPTIONAL MATCH (eq)-[:HAS_EQUIPMENT]->(child:Equipment)
    RETURN [n IN collect(DISTINCT child) WHERE n IS NOT NULL] AS subEquipment
  }

  CALL {
    WITH eq
    OPTIONAL MATCH (eq)-[:CONNECTS_TO]-(other:Equipment)
    RETURN [n IN collect(DISTINCT other) WHERE n IS NOT NULL] AS connections
  }

  CALL {
    WITH eq
    OPTIONAL MATCH (proc:Procedure)-[:PROCEDURE_FOR]->(eq)
    RETURN [n IN collect(DISTINCT proc) WHERE n IS NOT NULL] AS procedures
  }

${faultSymptomsCallBlock('eq')}

  CALL {
    WITH eq
    OPTIONAL MATCH (eq)-[:LOCATED_IN]->(a:Area)
    RETURN [n IN collect(DISTINCT a) WHERE n IS NOT NULL] AS areas
  }

  CALL {
    WITH eq
    OPTIONAL MATCH (recipe:Recipe)-[:USES_EQUIPMENT]->(eq)
    RETURN [n IN collect(DISTINCT recipe) WHERE n IS NOT NULL] AS mesRecipes
  }

  CALL {
    WITH eq
    OPTIONAL MATCH (phase:Phase)-[:USES_EQUIPMENT]->(eq)
    RETURN [n IN collect(DISTINCT phase) WHERE n IS NOT NULL] AS mesPhases
  }

  CALL {
    WITH eq
    OPTIONAL MATCH (batch:Batch)-[:RAN_ON]->(eq)
    RETURN [n IN collect(DISTINCT batch) WHERE n IS NOT NULL] AS mesBatches
  }

  CALL {
    WITH eq
    OPTIONAL MATCH (op:Operation)-[:RAN_ON|EXECUTED_ON]->(eq)
    RETURN [n IN collect(DISTINCT op) WHERE n IS NOT NULL] AS mesOperations
  }

  RETURN bomParts, bomEdges, subEquipment, connections, procedures, faults, areas,
         mesRecipes, mesPhases, mesBatches, mesOperations
  LIMIT 10000
`
